import { FrameError, LengthFramer, encodeLengthFrame } from "./framing";
import type { Transport, UnreliableLane } from "./transport";

/** The parts of the browser WebTransport API the transport uses (lets tests inject a fake). */
export interface WebTransportLike {
  readonly ready: Promise<unknown>;
  readonly closed: Promise<unknown>;
  readonly datagrams: {
    readonly readable: ReadableStream<Uint8Array>;
    readonly writable: WritableStream<Uint8Array>;
    readonly maxDatagramSize?: number;
  };
  createBidirectionalStream(): Promise<{
    readable: ReadableStream<Uint8Array>;
    writable: WritableStream<Uint8Array>;
  }>;
  close(info?: { closeCode?: number; reason?: string }): void;
}

export interface WebTransportOptions {
  serverCertificateHashes?: { algorithm: string; value: Uint8Array<ArrayBuffer> }[];
}

export type WebTransportFactory = (url: string, options: WebTransportOptions) => WebTransportLike;

/** True when the browser has the WebTransport API. */
export function webTransportSupported(): boolean {
  return typeof WebTransport === "function";
}

export const defaultWebTransportFactory: WebTransportFactory = (url, options) => new WebTransport(url, options);

function errorText(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message) return e.message;
  if (typeof e === "object" && e !== null && "reason" in e && typeof e.reason === "string" && e.reason) return e.reason;
  return fallback;
}

/**
 * WebTransport transport: every datagram is one unreliable message; the reliable channel is
 * the first client-opened bidirectional stream carrying `[u32 LE length][payload]` frames.
 * `certHashes` (SHA-256 digests) pin a self-signed development certificate.
 *
 * The datagram writable settles a write only after a round trip to the browser's network
 * process, and starts the next queued write after that. When that round trip is slow (a busy main
 * thread or an oversubscribed machine) the writes of a stream that is fed faster than it drains
 * queue up without bound, and every datagram, pings included, leaves seconds late. So at most one
 * write is in flight; behind it, only the newest datagram of each lane waits.
 */
export class WebTransportTransport implements Transport {
  readonly kind = "webtransport" as const;
  readonly ready: Promise<void>;
  onReliable: ((payload: Uint8Array) => void) | null = null;
  onUnreliable: ((payload: Uint8Array) => void) | null = null;
  onClose: ((reason: string) => void) | null = null;

  private readonly wt: WebTransportLike;
  private streamWriter: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private datagramWriter: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private opened = false;
  private finished = false;
  private maxDatagram = Infinity;
  private datagramBusy = false;
  /** Newest unsent datagram per lane, the lane that was updated longest ago first. */
  private readonly unsent = new Map<UnreliableLane, Uint8Array>();

  constructor(
    url: string,
    certHashes: readonly Uint8Array[] = [],
    create: WebTransportFactory = defaultWebTransportFactory,
  ) {
    const options: WebTransportOptions = {};
    if (certHashes.length > 0) {
      // Fresh ArrayBuffer-backed copies: the API takes a BufferSource.
      options.serverCertificateHashes = certHashes.map((value) => ({ algorithm: "sha-256", value: Uint8Array.from(value) }));
    }
    this.wt = create(url, options);
    const { promise, resolve, reject } = Promise.withResolvers<void>();
    this.ready = promise;
    promise.catch(() => undefined);
    void this.open(resolve, reject);
    this.wt.closed.then(
      (info) => this.fail(errorText(info, "WebTransport session closed"), reject),
      (e: unknown) => this.fail(errorText(e, "WebTransport session failed"), reject),
    );
  }

  private async open(resolve: () => void, reject: (e: Error) => void): Promise<void> {
    try {
      await this.wt.ready;
      const stream = await this.wt.createBidirectionalStream();
      if (this.finished) return;
      this.streamWriter = stream.writable.getWriter();
      this.datagramWriter = this.wt.datagrams.writable.getWriter();
      this.maxDatagram = this.wt.datagrams.maxDatagramSize ?? Infinity;
      this.opened = true;
      void this.readStream(stream.readable);
      void this.readDatagrams(this.wt.datagrams.readable);
      resolve();
    } catch (e) {
      this.fail(errorText(e, "WebTransport connection failed"), reject);
    }
  }

  private async readStream(readable: ReadableStream<Uint8Array>): Promise<void> {
    const framer = new LengthFramer();
    const reader = readable.getReader();
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        framer.push(value, (frame) => this.onReliable?.(frame));
      }
      this.fail("The server closed the reliable stream");
    } catch (e) {
      this.fail(e instanceof FrameError ? e.message : errorText(e, "Reliable stream failed"));
    }
  }

  private async readDatagrams(readable: ReadableStream<Uint8Array>): Promise<void> {
    const reader = readable.getReader();
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) return;
        if (!this.finished && value.length > 0) this.onUnreliable?.(value);
      }
    } catch {
      // The session `closed` promise reports the failure.
    }
  }

  private fail(reason: string, reject?: (e: Error) => void): void {
    if (!this.opened) reject?.(new Error(reason));
    if (this.finished) return;
    this.finished = true;
    this.onClose?.(reason);
  }

  sendReliable(payload: Uint8Array): void {
    if (this.finished || !this.streamWriter) return;
    this.streamWriter.write(encodeLengthFrame(payload)).catch(() => undefined);
  }

  sendUnreliable(payload: Uint8Array, lane: UnreliableLane = "input"): void {
    const writer = this.datagramWriter;
    if (this.finished || !writer || payload.length > this.maxDatagram) return;
    if (this.datagramBusy) {
      // A datagram behind a slow write is only worth sending fresh: it replaces an older unsent one.
      this.unsent.delete(lane);
      this.unsent.set(lane, payload);
      return;
    }
    this.writeDatagram(writer, payload);
  }

  private writeDatagram(writer: WritableStreamDefaultWriter<Uint8Array>, payload: Uint8Array): void {
    this.datagramBusy = true;
    writer.write(payload).then(
      () => {
        this.datagramBusy = false;
        const next = this.unsent.entries().next();
        if (next.done) return;
        this.unsent.delete(next.value[0]);
        if (!this.finished) this.writeDatagram(writer, next.value[1]);
      },
      () => this.unsent.clear(), // The datagram stream failed with the session, which `closed` reports.
    );
  }

  close(): void {
    this.finished = true;
    try {
      this.wt.close({ closeCode: 0, reason: "" });
    } catch {
      // Already closed.
    }
  }
}
